import { format } from 'date-fns'
import type { ScenarioRun, ScenarioRunsResponse } from '@/types/forklift'
import { getScenarioLabel } from '@/common/scenarioLabel'

// メトリクス画面の CSV (シナリオ実行 1 回 = 1 行)。
// 1 行目: ロボット ID と集計期間、2 行目: 列ヘッダ、3 行目以降: 実行ごとの行。
// 時刻は閲覧環境のローカル時刻 (日本時間での運用を想定)。
// ジャーナル対応前の日付の行 (daily-stats / webapp ログから合成) は荷役・エラー回数が
// 空欄になり、daily-stats 由来の行は分解像度なので時刻の秒が 00 になる。
// 複数機体をまとめて出す場合は No の次に「ロボットID」列を足し、機体ごとのブロックで並べる
// (No と累積は機体ごとの通算なので、機体をまたいで混ぜない)
export const SCENARIO_RUNS_CSV_COLUMNS = [
  'No',
  '年月日',
  '開始時刻',
  '終了時刻',
  'シナリオID',
  'シナリオ名',
  '実行モード',
  '荷積み元スロット',
  '荷下ろし先スロット',
  '完了フラグ（完了=1, 未完了=0）',
  '荷積み回数',
  '荷下ろし回数',
  'エラー発生回数',
  '実行時間（秒）',
  '走行距離（km）',
  '累積実行時間（秒）',
  '累積走行距離（km）',
] as const

// 実行モード (scenario_execute の executionMode / ジャーナルの scenario.executionMode)
const EXECUTION_MODE_LABELS: Record<string, string> = {
  initialize: '最初から',
  continue: '続きから',
  from_slot: '開始スロット指定',
  api: 'API',
}

// 実行モードはスロットエリア (set_slots) を使う PLC 連携シナリオで意味を持つ。
// プレイバックシナリオ (記録ルートの再生) では「続きから」等の区別が無いので空欄にする。
// 機体ジャーナルは PLC / コマンド起動の実行を一律 isPlaybackScenario=true にするため、
// その 2 つの起動元は表示対象に含める
// 完了したスロットを順に並べ、着手したが完了しなかった荷役があれば末尾に「(未完了)」を足す
// (例 "hira-1-1 / 301-202-0 (未完了)"。対象スロットが未解決なら "(未完了)" のみ)
// 荷役を検出できず順序から推定したスロット (pickup_inferred / place_inferred) も
// 実測と同じ表記で出す (推定の印は付けない)
function slotsLabel(
  slots: string[] | undefined,
  pending: boolean | undefined,
  pendingSlot: string | null | undefined
): string {
  const parts = [...(slots ?? [])]
  if (pending) parts.push(pendingSlot ? `${pendingSlot} (未完了)` : '(未完了)')
  return parts.join(' / ')
}

function executionModeLabel(run: ScenarioRun): string {
  const isPlc = run.trigger === 'plc' || run.trigger === 'command'
  if (run.is_playback_scenario && !isPlc) return ''
  return EXECUTION_MODE_LABELS[run.execution_mode] ?? run.execution_mode ?? ''
}

function escapeCsv(v: string | number): string {
  const s = String(v)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function formatSlashDate(yyyymmdd: string): string {
  return yyyymmdd.replace(/-/g, '/')
}

function toKm(meters: number | null): string {
  if (meters == null) return ''
  return String(Math.round((meters / 1000) * 100) / 100)
}

function toSec(ms: number | null): string {
  if (ms == null) return ''
  return String(Math.round(ms / 1000))
}

// 荷役・エラー回数: フォールバック行 (ジャーナル対応前) は不明なので空欄
function toCount(n: number | null): string | number {
  return n == null ? '' : n
}

// 行の表示名: 機体が解決した名前 → scenario-stats の名前 (daily-stats 補完済み) → ID ラベル
export function resolveRunScenarioName(
  run: ScenarioRun,
  nameById: Record<string, string>
): string {
  if (run.scenario_name) {
    return getScenarioLabel({ id: run.scenario_id, name: run.scenario_name })
  }
  if (run.scenario_id && nameById[run.scenario_id]) {
    return nameById[run.scenario_id]
  }
  return getScenarioLabel({ id: run.scenario_id, name: null })
}

function runToRow(
  run: ScenarioRun,
  nameById: Record<string, string>
): (string | number)[] {
  // 開始未観測 (期間前に開始・逆算不能) の行は終了時刻の日付を使う
  const anchorTs = run.start_ts ?? run.end_ts
  const anchor = anchorTs ? new Date(anchorTs) : null
  return [
    run.run_no,
    anchor ? format(anchor, 'yyyy/MM/dd') : '',
    run.start_ts ? format(new Date(run.start_ts), 'HH:mm:ss') : '',
    run.end_ts ? format(new Date(run.end_ts), 'HH:mm:ss') : '',
    run.scenario_id,
    resolveRunScenarioName(run, nameById),
    executionModeLabel(run),
    slotsLabel(run.pickup_slots, run.pending_pickup, run.pending_pickup_slot),
    slotsLabel(run.place_slots, run.pending_place, run.pending_place_slot),
    run.completed ? 1 : 0,
    toCount(run.pickups),
    toCount(run.places),
    toCount(run.errors),
    toSec(run.duration_ms),
    toKm(run.distance_m),
    toSec(run.cumulative_duration_ms),
    toKm(run.cumulative_distance_m),
  ]
}

function toCsvText(lines: (string | number)[][]): string {
  return (
    '\ufeff' + lines.map((row) => row.map(escapeCsv).join(',')).join('\r\n')
  )
}

export function buildScenarioRunsCsv(
  data: ScenarioRunsResponse,
  nameById: Record<string, string>
): string {
  const title = `ロボットID: ${data.fork_id}, 期間: ${formatSlashDate(
    data.period.start
  )} - ${formatSlashDate(data.period.end)}`
  const rows = data.runs.map((run) => runToRow(run, nameById))
  return toCsvText([[title], [...SCENARIO_RUNS_CSV_COLUMNS], ...rows])
}

// 複数機体分を 1 ファイルにまとめる。1 行目にロボット ID をすべて列挙し、
// 各行の 2 列目にロボット ID を入れる。行は機体ごとにまとめて No 順
export function buildMultiScenarioRunsCsv(
  list: ScenarioRunsResponse[],
  nameById: Record<string, string>
): string {
  if (list.length === 1) return buildScenarioRunsCsv(list[0], nameById)
  const period = list[0]?.period ?? { start: '', end: '' }
  const title = `ロボットID: ${list
    .map((d) => d.fork_id)
    .join(' / ')}, 期間: ${formatSlashDate(period.start)} - ${formatSlashDate(
    period.end
  )}`
  const [no, ...restColumns] = SCENARIO_RUNS_CSV_COLUMNS
  const header = [no, 'ロボットID', ...restColumns]
  const rows = list.flatMap((data) =>
    data.runs.map((run) => {
      const [runNo, ...rest] = runToRow(run, nameById)
      return [runNo, data.fork_id, ...rest]
    })
  )
  return toCsvText([[title], header, ...rows])
}
